import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import TaskTable from './TaskTable'
import TemplateTaskTables from './TemplateTaskTables'
import type { SearchLooseyTasksQuery, SearchTemplateTasksQuery, Task } from '@paintscout/api'
import { useSearchLooseyTasksQuery, useSearchTemplateTasksQuery } from '@paintscout/api'
import AddIcon from '@material-ui/icons/Add'
import { Sentry } from 'src/util/appSentry'
import { useSnackbar } from 'notistack'
import type { Action, StyleClasses } from '@paintscout/ui'
import { ActionButton, Button, Typography, useClientOptions, useIsMobile } from '@paintscout/ui'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { getTaskTemplateOptions } from '@paintscout/util/builder'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    taskDocHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.grey[200]}`
    },
    taskAggregateContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: theme.borderRadius.md,
      // all but first div get a left border
      '& > div:not(:first-child)': {
        borderLeft: `1px solid ${theme.palette.grey[200]}`,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
      },
      '& > div:first-child': {
        paddingRight: theme.spacing(2)
      },
      '& > div:last-child': {
        paddingRight: theme.spacing(0)
      }
    },
    aggregateSection: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }
  }),
  { name: 'DealTaskDocList' }
)

export interface DealTaskDocListProps {
  classes?: StyleClasses<typeof useStyles>
  dealId?: string
  query?: string
  dense?: boolean
  limit?: number
  title?: string
  enableFade?: boolean
  view?: 'upcoming' | 'overdue' | 'completed' | 'all' // used with enableFade to determine which tasks to fade
  page?: number
  sort?: string
  taskTemplateOrder: string[]
  addingTemplate?: { key: string; label: string } | null
  handleRequestSort?: (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => void
  onPageChange?: (newPage: number) => void
  onNewClick: (vals: { type: string; key?: string }) => void
  handleCreate?: () => void
  onTasksChange?: () => void
  onReorderTaskTemplates: (newOrder: string[]) => void
  newTasks?: Task[] | null
}

function DealTaskDocList({
  classes: classesProp,
  dealId,
  dense = true,
  enableFade,
  view,
  limit: argsLimit = 5,
  title,
  handleCreate,
  onTasksChange,
  page = 1,
  sort = 'startDateTime',
  taskTemplateOrder,
  addingTemplate,
  onPageChange,
  onNewClick,
  onReorderTaskTemplates,
  newTasks,
  handleRequestSort = (_ev: React.MouseEvent, _name: string, _direction: 'asc' | 'desc') => null
}: DealTaskDocListProps) {
  const classes = useStyles({ classes: classesProp })
  const [currentBookmark, setCurrentBookmark] = useState(null)
  const [currentPage, setCurrentPage] = useState(page)
  const { enqueueSnackbar } = useSnackbar()
  const limit = argsLimit
  const isMobile = useIsMobile()
  const { productionFlag } = useFlags()
  const { options } = useClientOptions()
  const taskTemplateOptions = getTaskTemplateOptions({ options, excludeEmpty: true })

  const hasTaskTemplates = productionFlag && taskTemplateOptions.length > 0

  const taskTemplateActions: Action[] = useMemo(() => {
    if (!hasTaskTemplates) {
      return undefined
    }

    return taskTemplateOptions.map((template) => ({
      label: template.label,
      key: template.key,
      sublabel: `${template.order.length} task${template.order.length !== 1 ? 's' : ''}`
    }))
  }, [taskTemplateOptions, hasTaskTemplates])

  // Query for loosey tasks (no template) - uses pagination
  const {
    data: looseyTasksData,
    loading: looseyLoading,
    error: looseyError,
    fetchMore: fetchMoreLooseyTasks,
    updateQuery: updateLooseyTasksQuery
  } = useSearchLooseyTasksQuery({
    variables: { query: `dealId:${dealId} AND -template.id:*`, sort, limit, skipSanitize: true },
    fetchPolicy: 'cache-and-network',
    skip: !dealId
  })

  // Query for template tasks - loads all (high limit, no pagination)
  const {
    data: templateTasksData,
    loading: templateLoading,
    error: templateError,
    updateQuery: updateTemplateTasksQuery
  } = useSearchTemplateTasksQuery({
    variables: { query: `dealId:${dealId} AND template.id:*`, sort, limit: 1000, skipSanitize: true },
    fetchPolicy: 'cache-and-network',
    skip: !dealId
  })

  useEffect(() => {
    if (!newTasks?.length) return

    const sortTasks = (tasks: Task[]) =>
      [...tasks].sort((a, b) => {
        const aHasDate = !!a.startDateTime
        const bHasDate = !!b.startDateTime
        if (aHasDate && !bHasDate) return -1
        if (!aHasDate && bHasDate) return 1
        if (!aHasDate && !bHasDate) return 0
        return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
      })

    const templateTasksToAdd = newTasks.filter((t) => !!(t.template as any)?.id)
    const looseyTasksToAdd = newTasks.filter((t) => !(t.template as any)?.id)

    if (templateTasksToAdd.length) {
      updateTemplateTasksQuery((cache: SearchTemplateTasksQuery) => {
        if (!cache?.searchTasks) return cache
        const existingIds = new Set(cache.searchTasks.tasks.map((t) => t._id))
        const incoming = templateTasksToAdd.filter((t) => !existingIds.has(t._id))
        if (!incoming.length) return cache
        return {
          ...cache,
          searchTasks: {
            ...cache.searchTasks,
            tasks: sortTasks([...cache.searchTasks.tasks, ...incoming]),
            total_rows: cache.searchTasks.total_rows + incoming.length
          }
        }
      })
    }

    if (looseyTasksToAdd.length) {
      updateLooseyTasksQuery((cache: SearchLooseyTasksQuery) => {
        if (!cache?.searchTasks) return cache
        const existingIds = new Set(cache.searchTasks.tasks.map((t) => t._id))
        const incoming = looseyTasksToAdd.filter((t) => !existingIds.has(t._id))
        if (!incoming.length) return cache
        return {
          ...cache,
          searchTasks: {
            ...cache.searchTasks,
            tasks: sortTasks([...cache.searchTasks.tasks, ...incoming]),
            total_rows: cache.searchTasks.total_rows + incoming.length
          }
        }
      })
    }
  }, [newTasks]) // eslint-disable-line react-hooks/exhaustive-deps

  const error = looseyError || templateError

  const { looseyTasks, looseyTotalRows } = useMemo(() => {
    setCurrentBookmark(looseyTasksData?.searchTasks?.bookmark ?? null)
    return {
      looseyTasks: looseyTasksData?.searchTasks?.tasks ?? [],
      looseyTotalRows: looseyTasksData?.searchTasks?.total_rows ?? 0
    }
  }, [looseyTasksData])

  const templateTasks: { [key: string]: Task[] } = useMemo(() => {
    const tasks = templateTasksData?.searchTasks?.tasks ?? []
    const grouped: { [key: string]: any[] } = {}
    tasks.forEach((task) => {
      const key = task.template?.id
      if (key) {
        if (!grouped[key]) {
          grouped[key] = []
        }
        grouped[key].push(task)
      }
    })
    return grouped
  }, [templateTasksData])

  const orderedTemplateKeys = useMemo(() => {
    const allKeys = Object.keys(templateTasks)
    if (taskTemplateOrder.length === 0) return allKeys
    const saved = taskTemplateOrder.filter((id) => allKeys.includes(id))
    const unsaved = allKeys.filter((id) => !taskTemplateOrder.includes(id))
    return [...saved, ...unsaved]
  }, [templateTasks, taskTemplateOrder])

  // Slice loosey tasks for current page display
  const paginatedLooseyTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * limit
    const endIndex = startIndex + limit
    return looseyTasks.slice(startIndex, endIndex)
  }, [looseyTasks, currentPage, limit])

  // Combined tasks for aggregate counts
  const allTasks = useMemo(() => {
    return [...looseyTasks, ...(templateTasksData?.searchTasks?.tasks ?? [])]
  }, [looseyTasks, templateTasksData])

  useEffect(() => {
    if (page == 1 && page !== currentPage) {
      setCurrentPage(page)
    }
  }, [page])

  if (error) {
    Sentry.captureException(error, {
      tags: { dealId }
    })
    enqueueSnackbar('Unable to load Tasks', { variant: 'error' })
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    onPageChange?.(newPage)
    if (newPage === 1) {
      setCurrentBookmark(null)
    }
  }

  const handleFetchNextPage = (page: number) => {
    const nextPageFirstIndex = (page - 1) * limit
    const dataExists = !!looseyTasks[nextPageFirstIndex]
    if (dataExists) {
      return
    }

    fetchMoreLooseyTasks({
      variables: {
        limit: limit,
        from: currentPage * limit,
        bookmark: currentBookmark
      },
      updateQuery: (prev: SearchLooseyTasksQuery, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        setCurrentBookmark(fetchMoreResult.searchTasks.bookmark)
        return Object.assign({}, prev, {
          searchTasks: {
            ...prev.searchTasks,
            tasks: [...prev.searchTasks.tasks, ...fetchMoreResult.searchTasks.tasks],
            bookmark: fetchMoreResult.searchTasks.bookmark
          }
        })
      }
    })
  }

  function removeRowsFromLooseyQuery(ids: string[]) {
    onTasksChange?.()

    updateLooseyTasksQuery((cache: SearchLooseyTasksQuery) => {
      if (!cache?.searchTasks) {
        return cache
      }

      const filteredTasks = cache.searchTasks.tasks.filter((task) => !ids.includes(task._id))
      return {
        ...cache,
        searchTasks: {
          ...cache.searchTasks,
          tasks: filteredTasks,
          total_rows: cache.searchTasks.total_rows - (cache.searchTasks.tasks.length - filteredTasks.length)
        }
      }
    })
  }

  function removeRowsFromTemplateQuery(ids: string[]) {
    onTasksChange?.()

    updateTemplateTasksQuery((cache: SearchTemplateTasksQuery) => {
      if (!cache?.searchTasks) {
        return cache
      }

      const filteredTasks = cache.searchTasks.tasks.filter((task) => !ids.includes(task._id))
      return {
        ...cache,
        searchTasks: {
          ...cache.searchTasks,
          tasks: filteredTasks,
          total_rows: cache.searchTasks.total_rows - (cache.searchTasks.tasks.length - filteredTasks.length)
        }
      }
    })
  }

  function updateRowsInLooseyQuery(task: any) {
    onTasksChange?.()
    updateLooseyTasksQuery((cache: SearchLooseyTasksQuery) => {
      if (!cache?.searchTasks) {
        return cache
      }

      return {
        ...cache,
        searchTasks: {
          ...cache.searchTasks,
          tasks: cache.searchTasks.tasks
            .map((t) =>
              t._id === task._id
                ? (() => {
                    if (task.dealId !== dealId) {
                      return null
                    } else {
                      return task
                    }
                  })()
                : t
            )
            .filter(Boolean)
            .sort((a, b) => {
              const aHasDate = !!a.startDateTime
              const bHasDate = !!b.startDateTime
              if (aHasDate && !bHasDate) return -1
              if (!aHasDate && bHasDate) return 1
              if (!aHasDate && !bHasDate) return 0
              return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
            })
        }
      }
    })
  }

  function updateRowsInTemplateQuery(task: any) {
    onTasksChange?.()
    updateTemplateTasksQuery((cache: SearchTemplateTasksQuery) => {
      if (!cache?.searchTasks) {
        return cache
      }

      return {
        ...cache,
        searchTasks: {
          ...cache.searchTasks,
          tasks: cache.searchTasks.tasks
            .map((t) =>
              t._id === task._id
                ? (() => {
                    if (task.dealId !== dealId) {
                      return null
                    } else {
                      return task
                    }
                  })()
                : t
            )
            .filter(Boolean)
            .sort((a, b) => {
              const aHasDate = !!a.startDateTime
              const bHasDate = !!b.startDateTime
              if (aHasDate && !bHasDate) return -1
              if (!aHasDate && bHasDate) return 1
              if (!aHasDate && !bHasDate) return 0
              return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
            })
        }
      }
    })
  }

  const completed = allTasks.filter((task) => task.completed)?.length
  const remaining = allTasks.filter((task) => !task.completed)?.length

  const taskAggregate = useMemo(
    () => (
      <div className={classes.taskAggregateContainer}>
        <div className={classes.aggregateSection}>
          <Typography value={allTasks.length} variant="h3" showZeros />
          <Typography variant="subtitle1">Total</Typography>
        </div>
        <div className={classes.aggregateSection}>
          <Typography value={completed} variant="h3" showZeros />
          <Typography variant="subtitle1">Completed</Typography>
        </div>
        <div className={classes.aggregateSection}>
          <Typography value={remaining} variant="h3" showZeros />
          <Typography variant="subtitle1">Remaining</Typography>
        </div>
      </div>
    ),
    [classes.aggregateSection, classes.taskAggregateContainer, allTasks.length, completed, remaining]
  )

  const NewButton = useMemo(() => {
    if (isMobile) {
      return null
    }

    if (taskTemplateActions) {
      return (
        <ActionButton
          className={classes?.newButton}
          actions={[
            { label: 'Single Task', sublabel: 'Create a one-off task', key: 'new-task' },
            { key: 'divider', label: '' },
            ...taskTemplateActions
          ]}
          onActionClick={(_ev, key) => {
            switch (key) {
              case 'new-task':
                onNewClick({ type: 'task' })
                break

              default:
                onNewClick({ type: 'taskTemplate', key })
                break
            }
          }}
          button={
            <Button
              key={'new-task-action-button'}
              className={classes?.newButton}
              variant="hoverUnderline"
              color="primary"
              startIcon={<AddIcon />}
            >
              New
            </Button>
          }
        />
      )
    } else {
      return (
        <Button
          onClick={() => onNewClick({ type: 'task' })}
          className={classes?.newButton}
          variant="hoverUnderline"
          color="primary"
          startIcon={<AddIcon />}
        >
          New
        </Button>
      )
    }
  }, [classes?.newButton, isMobile, onNewClick, taskTemplateActions])

  return (
    <div className={classes.root}>
      <div className={classes.taskDocHeader}>
        {taskAggregate}
        {NewButton}
      </div>
      <TemplateTaskTables
        templateTasks={templateTasks}
        orderedTemplateKeys={orderedTemplateKeys}
        loading={templateLoading}
        dense={dense}
        enableFade={enableFade}
        view={view}
        addingTemplate={addingTemplate}
        onDelete={removeRowsFromTemplateQuery}
        onEdit={updateRowsInTemplateQuery}
        onComplete={onTasksChange}
        onReorderTaskTemplates={onReorderTaskTemplates}
      />
      <TaskTable
        classes={classes}
        handleCreate={handleCreate}
        title={title}
        tasks={paginatedLooseyTasks}
        loading={looseyLoading}
        dense={dense}
        totalRows={looseyTotalRows}
        page={currentPage}
        sort={sort}
        rowsPerPage={limit}
        pages={Math.ceil(looseyTotalRows / limit)}
        disablePagination={looseyTotalRows <= limit}
        onPageChange={handlePageChange}
        allowCustomColumns={true}
        actions={{
          edit: true,
          viewDeal: true,
          delete: true
        }}
        actionsForRow={(row) => ({
          delete: true,
          viewDeal: !!row.dealId,
          edit: true
        })}
        onFetchNextPage={handleFetchNextPage}
        onDelete={removeRowsFromLooseyQuery}
        onEdit={updateRowsInLooseyQuery}
        onComplete={onTasksChange}
        onRequestSort={handleRequestSort}
        enableFade={enableFade}
        view={view}
        noResultsMessage={Object.keys(templateTasks).length > 0 ? '' : 'No tasks found.'}
      />
    </div>
  )
}

export default DealTaskDocList
